/**
 * Report-Definition für die Retrospektive.
 *
 * Übersetzt den geladenen Retro-Datenstand in ein {@link ReportDocument}.
 * Analog zu {@link ./projektauftragReport}: deklariert nur den Inhalt, der
 * gemeinsame Renderer erzeugt das PDF. Pro Spalte (Kategorie) eine Section mit
 * den Einträgen als Liste — lesbarer als eine durchgehende Tabelle.
 */
import {
  buildDocument,
  type ReportBlock,
  type ReportDocument,
  type ReportSection,
} from '../../../lib/pdf/reportModel';

export type RetroSpalte = 'good' | 'imp' | 'idea' | 'act';

export interface RetroReportItem {
  spalte: RetroSpalte;
  inhalt: string;
  autor?: string | null;
}

export interface RetroReportInput {
  items: RetroReportItem[];
}

const COLUMN_LABEL: Record<RetroSpalte, string> = {
  good: 'Gut gelaufen',
  imp: 'Verbessern',
  idea: 'Ideen',
  act: 'Aktionen',
};

// act (Maßnahmen) hat keine eigene Spalte mehr (PHQ-93): die Einträge laufen unter Try (idea).
const COLUMN_ORDER: RetroSpalte[] = ['good', 'imp', 'idea'];

export function buildRetroReport(input: RetroReportInput): ReportDocument {
  const hasAny = input.items.length > 0;

  const sections: (ReportSection | null)[] = COLUMN_ORDER.map((spalte) => {
    const entries = input.items.filter((it) => it.spalte === spalte || (spalte === 'idea' && it.spalte === 'act'));
    if (entries.length === 0) return null;
    const blocks: ReportBlock[] = entries
      .map((it) => {
        const suffix = it.autor && it.autor.trim() ? ` — ${it.autor.trim()}` : '';
        return `${it.inhalt}${suffix}`.trim();
      })
      .filter((v) => v.length > 0)
      .map((value) => ({ kind: 'text', value }));
    if (blocks.length === 0) return null;
    return { title: COLUMN_LABEL[spalte], blocks } satisfies ReportSection;
  });

  if (!hasAny) {
    return buildDocument([
      { title: 'Retrospektive', blocks: [{ kind: 'note', value: 'Noch keine Retro-Einträge erfasst.' }] },
    ]);
  }

  return buildDocument(sections);
}
